import React, { useState } from 'react';
import { Globe, Activity, BarChart2 } from 'lucide-react';
import { Domain } from '../../../types';
import { usePermission } from '../../../hooks/useAuth';
import { PageHeader } from '@kioskgaming/ui';
import AdminTabs, { type AdminTabItem } from '../../ui/AdminTabs';
import AccessDeniedPanel from '../../ui/AccessDeniedPanel';
import DomainList from './DomainList';
import CreateDomainModal from './CreateDomainModal';
import UpdateDomainModal from './UpdateDomainModal';
import DeleteDomainModal from './DeleteDomainModal';
import KioskHealthTab from './KioskHealthTab';
import DomainAnalyticsTab from './DomainAnalyticsTab';

type TabId = 'domains' | 'kiosk-health' | 'analytics';

const DomainsManagement: React.FC = () => {
  const canManage = usePermission('domainManagement');
  const [activeTab, setActiveTab] = useState<TabId>('domains');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  if (!canManage) {
    return (
      <AccessDeniedPanel message="You don&apos;t have permission to manage domains." />
    );
  }

  const tabs: AdminTabItem<TabId>[] = [
    { id: 'domains', label: 'Domains', icon: Globe },
    { id: 'kiosk-health', label: 'Kiosk Health', icon: Activity },
    { id: 'analytics', label: 'Analytics', icon: BarChart2 },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Domain Management"
        description="Manage registered domains, monitor kiosk health, and view per-domain analytics"
      />

      <AdminTabs<TabId>
        variant="underline"
        tabs={tabs}
        value={activeTab}
        onChange={setActiveTab}
      />

      {activeTab === 'domains' && (
        <>
          <DomainList
            onCreate={() => setIsCreateOpen(true)}
            onEdit={(d) => { setSelectedDomain(d); setIsUpdateOpen(true); }}
            onDelete={(d) => { setSelectedDomain(d); setIsDeleteOpen(true); }}
          />
          <CreateDomainModal
            isOpen={isCreateOpen}
            onClose={() => setIsCreateOpen(false)}
            onSuccess={() => {}}
          />
          <UpdateDomainModal
            domain={selectedDomain}
            isOpen={isUpdateOpen}
            onClose={() => { setIsUpdateOpen(false); setSelectedDomain(null); }}
            onSuccess={() => setSelectedDomain(null)}
          />
          <DeleteDomainModal
            domain={selectedDomain}
            isOpen={isDeleteOpen}
            onClose={() => { setIsDeleteOpen(false); setSelectedDomain(null); }}
            onSuccess={() => setSelectedDomain(null)}
          />
        </>
      )}

      {activeTab === 'kiosk-health' && <KioskHealthTab />}

      {activeTab === 'analytics' && <DomainAnalyticsTab />}
    </div>
  );
};

export default DomainsManagement;
